import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';

import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { ControllerProps } from 'src/router';
import { ConfigurationService } from 'src/core/services/configuration.service';
import { DataLocalService } from 'src/core/services/data-local.service';
import { TaxonomyDataService } from 'src/app/general/taxonomies/form/taxonomy-form.data';
import { TaxonomyFormView } from 'src/app/general/taxonomies/form/taxonomy-form.view';

interface Props extends ControllerProps<{id: string}> {
}

interface State {
	mode: ControlMode;
	defaultLanguage: string;
}

@observer
export class TaxonomyFormController extends React.Component<Props, State> {
	@resolve
	private configurationService: ConfigurationService;

	@resolve
	private toast: ToastNotifications;

	@observable
	dataService: TaxonomyDataService;

	@observable
	tagsDataService: DataLocalService<TaxonomyTagItem>;

	constructor(props: Readonly<Props>) {
		super(props);

		this.state = {
			mode: 'display',
			defaultLanguage: ''
		};
	}

	componentDidMount() {
		this.props.setBusy(true);

		this.loadConfiguration()
			.then(() => this.loadData())
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
			});
	}

	private async loadConfiguration(): Promise<void> {
		const appConfig = await this.configurationService.current();

		this.setState({
			defaultLanguage: appConfig.defaultLanguage
		});
	}

	private async loadData(): Promise<void> {
		this.dataService = TaxonomyDataService.make();
		const { id } = this.props.match.params;
		const { defaultLanguage } = this.state;

		if (id === 'new') {
			// Create new
			await this.dataService.add(defaultLanguage);
			this.handleModeChange('edit');
		} else {
			// Load entity
			const taxonomyId = parseInt(id);
			this.dataService.addFilter('id', 'eq', taxonomyId);
			this.dataService.addSorting('id', 'asc', true);
			this.dataService.pageSize = 1;

			await this.dataService.load(query => query);
		}

		const tags = this.dataService.item.dynamicProperties.taxonomyTags;
		this.tagsDataService = new DataLocalService(tags);
		// TODO: Create tags DataLocalService
	}

	@computed
	get title() {
		if (this.dataService && this.dataService.item && this.dataService.item.dynamicProperties.displayName) {
			return `Taxonomy ${this.dataService.item.dynamicProperties['displayName'][this.state.defaultLanguage]}`;
		}

		return '';
	}

	handleModeChange = (mode: ControlMode): void => {
		this.setState({ mode });
	};

	handleSave = async (): Promise<void> => {
		const item = this.dataService.item;
		const errors = await item.validate(false);
		if (errors.length > 0) {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
			return;
		}

		this.props.setBusy(true);
		try {
			item.dynamicProperties.taxonomyTags = this.tagsDataService.items.slice();

			await this.dataService.save();
			this.toast.showDataSaved();
			this.props.history.replace(`/general/taxonomies/${item.entity.id}`);
		} catch (error) {
			this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
		}

		this.props.setBusy(false);
	};

	handleCancel = (): void => {
		this.props.history.push('/general/taxonomies');
	};

	render() {
		if (this.dataService == null || this.dataService.item == null || this.tagsDataService == null) {
			return null;
		}

		const { mode, defaultLanguage } = this.state;
		const { setBusy } = this.props;

		return (
			<TaxonomyFormView
				model={this.dataService.item}
				mode={mode}
				onModeChange={this.handleModeChange}
				onSave={this.handleSave}
				onCancel={this.handleCancel}
				setBusy={setBusy}
				title={this.title}
				defaultLanguage={defaultLanguage}
				tags={this.tagsDataService}
				contextService={this.dataService}
			/>
		);
	}
}



// WEBPACK FOOTER //
// ./src/app/general/taxonomies/form/taxonomy-form.controller.tsx